import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { BackendTables } from "interfaces/db";
import { FilterOperator, SessionListResponse, TypedResultMap } from "interfaces/sessions";
import { ReviewStatusColumns, SessionV2Columns } from "interfaces/table";
import { getServicesManager } from "services";

// Redux-safe version of SessionListResponse with serializable date and map
export type ReduxSafeSessionListResponse = Omit<SessionListResponse, 'start' | 'result'> & {
  start: string; // ISO string instead of Date
  result: Record<string, any>; // Plain object instead of TypedResultMap
};

// Conversion functions
export function toReduxSafeSessionListResponse(response: SessionListResponse): ReduxSafeSessionListResponse {
  // Convert TypedResultMap to plain object, serializing Date values
  const resultObject: Record<string, any> = {};
  response.result.forEach((value, key) => {
    // Convert Date objects to ISO strings for serialization
    if (value instanceof Date) {
      resultObject[key] = value.toISOString();
    } else {
      resultObject[key] = value;
    }
  });
  
  const startValue = response.start instanceof Date ? response.start.toISOString() : 
                     (typeof response.start === 'string' ? response.start : new Date().toISOString());
  
  return {
    session_id: response.session_id,
    user_id: response.user_id,
    conversation_type: response.conversation_type,
    customer_address: response.customer_address,
    agent_address: response.agent_address,
    start: startValue, // Use our converted string value
    result: resultObject
  };
}

export function fromReduxSafeSessionListResponse(response: ReduxSafeSessionListResponse): SessionListResponse {
  // Convert plain object back to TypedResultMap, deserializing Date values
  const resultMap = new TypedResultMap();
  
  // Use Map.set with proper typing by accessing the parent Map.set method
  const mapSet = Map.prototype.set.bind(resultMap);
  
  Object.entries(response.result).forEach(([key, value]) => {
    // Convert ISO string dates back to Date objects for date columns
    const dateColumnNames = [
      'started_at', 'created_at', 'scheduled_start', 'opened_at',
      'customer_joined_at', 'customer_live_at', 'customer_live_known_at', 'first_user_joined_at',
      'callback_timing'
    ];
    
    if (typeof value === 'string' && dateColumnNames.some(dateCol => key.includes(dateCol))) {
      // Try to parse as ISO date string
      const dateValue = new Date(value);
      if (!isNaN(dateValue.getTime())) {
        mapSet(key, dateValue);
      } else {
        mapSet(key, value);
      }
    } else {
      mapSet(key, value);
    }
  });
  
  return {
    ...response,
    start: new Date(response.start),
    result: resultMap
  };
}

export function toReduxSafeSessionListResponses(responses: SessionListResponse[]): ReduxSafeSessionListResponse[] {
  return responses.map(toReduxSafeSessionListResponse);
}

export function fromReduxSafeSessionListResponses(responses: ReduxSafeSessionListResponse[]): SessionListResponse[] {
  return responses.map(fromReduxSafeSessionListResponse);
}

export interface SessionState {
  items: ReduxSafeSessionListResponse[];
  startRequested: string | null; // ISO string instead of Date
  hasMore: boolean;
  startHasMore: boolean;
  isLoading: boolean;
  isDebouncing: boolean;
  error: string | null;
  lastRequestKey: string | null;
}

interface SessionsState {
  sessions: { [key: string]: SessionState };
  itemsPerPage: number;
}

const initialState: SessionsState = {
  sessions: {},
  itemsPerPage: 50
};

export const sessionListSlice = createSlice({
  name: 'sessionList',
  initialState,
  reducers: {
    startLoading: (state, action: PayloadAction<{ key: string, lastRequestKey: string }>) => {
      const { key, lastRequestKey } = action.payload;
      if (!state.sessions[key]) {
        state.sessions[key] = {
          items: [],
          startRequested: null,
          hasMore: true,
          startHasMore: true,
          isLoading: true,
          isDebouncing: false,
          error: null,
          lastRequestKey: null
        };
      } else {
        state.sessions[key].isLoading = true;
        state.sessions[key].isDebouncing = false;
        state.sessions[key].error = null;
        state.sessions[key].lastRequestKey = lastRequestKey;
      }
    },
    
    startDebouncing: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      if (!state.sessions[key]) {
        state.sessions[key] = {
          items: [],
          startRequested: null,
          hasMore: true,
          startHasMore: true,
          isLoading: false,
          isDebouncing: true,
          error: null,
          lastRequestKey: null
        };
      } else {
        state.sessions[key].isDebouncing = true;
        state.sessions[key].error = null;
      }
    },
    
    stopDebouncing: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      if (state.sessions[key]) {
        state.sessions[key].isDebouncing = false;
      }
    },

    updateHasMore: (state, action: PayloadAction<{ key: string, hasMore: boolean }>) => {
      const { key, hasMore } = action.payload;
      state.sessions[key].hasMore = hasMore;
    },
    updateStartHasMore: (state, action: PayloadAction<{ key: string, startHasMore: boolean }>) => {
      const { key, startHasMore } = action.payload;
      state.sessions[key].startHasMore = startHasMore;
    },
    sessionsLoaded: (state, action: PayloadAction<{
      key: string;
      items: ReduxSafeSessionListResponse[];
      startRequested: string | null;
      startHasMore: boolean;
      hasMore: boolean;
      isForceRefresh?: boolean;
      lastRequestKey: string;
    }>) => {
      const { key, items, startRequested, hasMore, startHasMore, isForceRefresh, lastRequestKey } = action.payload;
      const existingItems = !isForceRefresh ? state.sessions[key]?.items ?? [] : [];
      // Items are already in Redux-safe format
      const reduxSafeItems = items;
      
      // Ensure existing items are also Redux-safe by sanitizing any potential Date objects
      const sanitizedExistingItems = existingItems.map(item => {
        const sanitizedResult: Record<string, any> = {};
        Object.entries(item.result).forEach(([key, value]) => {
          if (value instanceof Date) {
            sanitizedResult[key] = value.toISOString();
          } else {
            sanitizedResult[key] = value;
          }
        });
        
        return {
          ...item,
          result: sanitizedResult
        };
      });
      
      const lastItemStart = sanitizedExistingItems.length > 0 ? sanitizedExistingItems[sanitizedExistingItems.length - 1].result[SessionV2Columns.STARTED_AT] : null;
      
      // Filter out duplicates when appending new items
      const currentSessionIds = new Set(sanitizedExistingItems.map(x => x.session_id));
      const newItems = (lastItemStart == null ? reduxSafeItems : reduxSafeItems.filter(x => !currentSessionIds.has(x.session_id)));

      state.sessions[key] = {
        items: [...sanitizedExistingItems, ...newItems],
        startRequested: startRequested,
        hasMore,
        startHasMore,
        isLoading: false,
        isDebouncing: false,
        error: null,
        lastRequestKey
      };
    },
    loadingFailed: (state, action: PayloadAction<{ key: string; error: string }>) => {
      const { key, error } = action.payload;
      if (!state.sessions[key]) {
        state.sessions[key] = {
          items: [],
          startRequested: null,
          startHasMore: false,
          hasMore: false,
          isLoading: false,
          isDebouncing: false,
          error: null,
          lastRequestKey: null
        };
      } else {
        state.sessions[key].isLoading = false;
        state.sessions[key].isDebouncing = false;
        state.sessions[key].error = error;
        state.sessions[key].lastRequestKey = null;
      }
    },
    clearSessions: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      delete state.sessions[key];
    },
    clearAllSessions: (state) => {
      state.sessions = {};
    }
  },
});

export const { 
  startLoading,
  startDebouncing,
  stopDebouncing,
  sessionsLoaded,
  loadingFailed,
  clearSessions,
  clearAllSessions,
  updateHasMore,
  updateStartHasMore
} = sessionListSlice.actions;


export interface RefreshState {
  forceRefresh: number
}
  
const refreshInitialState: RefreshState = {
  forceRefresh: 0
};

export const refreshSlice = createSlice({
  name: 'refresh',
  initialState: refreshInitialState ,
  reducers: {
    updateForceRefresh: (state) => {
      state.forceRefresh += 1
    }
  },
});

export const { updateForceRefresh } = refreshSlice.actions;
export interface PendingReviewsState {
  value: number | null;
}
const pendingReviewsInititalState: PendingReviewsState = {
  value: null,
};

export const pendingReviewsSlice = createSlice({
  name: 'pendingReviews',
  initialState: pendingReviewsInititalState,
  reducers: {
    updatePendingReviews: (state, action: PayloadAction<PendingReviewsState>) => {
      state.value = action.payload.value;
    },
  }
});

export const { updatePendingReviews } = pendingReviewsSlice.actions;


export function reloadPendingReviewsInitialState(dispatch: Dispatch) {
  const result = getServicesManager().getListUserSessionsV5({
    'start': new Date(new Date().valueOf() - 28 * 60 * 60 * 24 * 1000),
    'end': new Date(),
    'cnf': [[{
      table: BackendTables.REVIEW_STATUSES,
      column_name: ReviewStatusColumns.IS_OPEN,
      operator: FilterOperator.EQ,
      value_safe: true,
    }]],
    'select': [
        {
          column_name: SessionV2Columns.SESSION_ID,
          table: BackendTables.SESSIONS_V2
        }, {
          column_name: SessionV2Columns.STARTED_AT,
          table: BackendTables.SESSIONS_V2
        }, {
          column_name: SessionV2Columns.AGENT_ADDRESS,
          table: BackendTables.SESSIONS_V2
        }, {
          column_name: SessionV2Columns.CUSTOMER_ADDRESS,
          table: BackendTables.SESSIONS_V2
        }, {
          column_name: SessionV2Columns.RESOURCE_ID,
          table: BackendTables.SESSIONS_V2
        }, {
          column_name: SessionV2Columns.CONVERSATION_TYPE,
          table: BackendTables.SESSIONS_V2
      }] 
  });
  result.then((v) => {
    if (v) {
      dispatch(pendingReviewsSlice.actions.updatePendingReviews({ 'value': v.length }));
    }
  });
}
